import { computed } from 'vue'
import { priceList } from '@/constant/priceList'
import type { FormData } from '@/types/index'

export function usePriceCalculations (form: FormData) {
  const getPrice = <T>(value: T, priceMap: Record<string, number> | undefined): number => {
    return priceMap && typeof value === 'string' && value in priceMap ? priceMap[value] : 0
  }

  const calculateBaseWebsitePrice = (): number => {
    let price = 0
    price += getPrice(form.builder, priceList.website.builder)
    price += getPrice(form.uniquePages, priceList.website.uniquePages)
    price += getPrice(form.designPreference, priceList.website.designPreference)
    price += getPrice(form.mobileApps, priceList.website.mobileApps)
    price += getPrice(form.languages, priceList.website.languages)
    price += form.features?.contentEditor ? priceList.website.features.contentEditor : 0
    price += form.features?.hosting ? priceList.website.features.hosting : 0
    price += form.features?.domain ? priceList.website.features.domain : 0
    price += form.features?.webAd ? priceList.website.features.webAd : 0
    price += form.features?.seo ? priceList.website.features.seo : 0
    price += form.features?.businessEmail ? priceList.website.features.businessEmail : 0
    price += form.features?.ecommerce ? priceList.website.features.ecommerce : 0
    return price
  }

  const calculateWebsiteMaintenance = (): { yearly: number } => {
    let yearly = 0
    if (form.builder !== 'no') {
      yearly += priceList.website.maintenance // Fixed 2,400,000 IDR
    }
    return { yearly }
  }

  const calculateBaseDesignPrice = (): number => {
    let price = 0
    price += getPrice(form.logo, priceList.design.logo)
    price += form.logoFeatures?.businessCard ? priceList.design.logoFeatures.businessCard : 0
    price += form.logoFeatures?.stationeries ? priceList.design.logoFeatures.stationeries : 0
    price += form.logoFeatures?.idCard ? priceList.design.logoFeatures.idCard : 0
    price += form.logoFeatures?.customMockups ? priceList.design.logoFeatures.customMockups : 0
    price += getPrice(form.packaging, priceList.design.packaging)
    price += getPrice(form.photography, priceList.design.photography)
    price += getPrice(form.catalog, priceList.design.catalog)
    price += getPrice(form.marketplace, priceList.design.marketplace)
    price += getPrice(form.mascot2d, priceList.design.mascot2d)
    price += getPrice(form.mascot3d, priceList.design.mascot3d)
    return price
  }

  const calculateDesignSocialMedia = (): { base: number, servicePrice: number, frequency: string | null } => {
    let base = 0
    let servicePrice = 0
    let frequency: string | null = null

    if (!form.socialMedia) {
      base += priceList.design.socialMedia.no
    } else {
      if (form.socialMedia.type === 'no') {
        base += priceList.design.socialMedia.no
      } else if (form.socialMedia.type === 'fixed') {
        base += getPrice(form.socialMedia.count, priceList.design.socialMedia.fixed)
      } else if (form.socialMedia.type === 'regularly' && form.socialMedia.frequency) {
        const freq = form.socialMedia.frequency as 'monthly' | 'weekly'
        const price = getPrice(form.socialMedia.count, priceList.design.socialMedia.regularly[freq])
        servicePrice = price
        frequency = freq
      }
    }
    return { base, servicePrice, frequency }
  }

  const calculateBaseVideoPrice = (): number => {
    let price = 0
    if (!form.shortVideo || form.shortVideo.type === 'no') {
      price += priceList.video.shortVideo.no
    } else if (form.shortVideo.type === 'fixed') {
      price += getPrice(form.shortVideo.count, priceList.video.shortVideo.fixed)
    }

    if (!form.liveStreaming || form.liveStreaming.type === 'no') {
      price += priceList.video.liveStreaming.no
    } else if (form.liveStreaming.type === 'fixed') {
      price += getPrice(form.liveStreaming.count, priceList.video.liveStreaming.fixed)
    }

    return price
  }

  const calculateVideoRecurring = (): { videoMonthlyServicePrice: number, videoWeeklyServicePrice: number } => {
    let videoMonthlyServicePrice = 0
    let videoWeeklyServicePrice = 0

    if (form.shortVideo?.type === 'regularly' && form.shortVideo.frequency) {
      const freq = form.shortVideo.frequency as 'monthly' | 'weekly'
      const price = getPrice(form.shortVideo.count, priceList.video.shortVideo.regularly[freq])
      if (freq === 'monthly') videoMonthlyServicePrice += price
      if (freq === 'weekly') videoWeeklyServicePrice += price
    }

    if (form.liveStreaming?.type === 'regularly' && form.liveStreaming.frequency) {
      const freq = form.liveStreaming.frequency as 'monthly' | 'weekly'
      const price = getPrice(form.liveStreaming.count, priceList.video.liveStreaming.regularly[freq])
      if (freq === 'monthly') videoMonthlyServicePrice += price
      if (freq === 'weekly') videoWeeklyServicePrice += price
    }

    return { videoMonthlyServicePrice, videoWeeklyServicePrice }
  }

  // Final calculations
  const websitePrice = computed((): number => {
    return calculateBaseWebsitePrice()
  })

  const websiteMaintenance = computed((): number => {
    return calculateWebsiteMaintenance().yearly
  })

  const designPrice = computed((): number => {
    const baseDesign = calculateBaseDesignPrice()
    const socialMedia = calculateDesignSocialMedia()
    return baseDesign + socialMedia.base
  })

  const designSocialMediaService = computed((): { price: number, frequency: string | null } => {
    const { servicePrice, frequency } = calculateDesignSocialMedia()
    return { price: servicePrice, frequency }
  })

  const videoPrice = computed((): number => {
    return calculateBaseVideoPrice()
  })

  const videoServices = computed((): { videoMonthlyServicePrice: number, videoWeeklyServicePrice: number } => {
    return calculateVideoRecurring()
  })

  const yearlyTotal = computed((): number => {
    return websiteMaintenance.value // Only website maintenance is yearly
  })

  const monthlyTotal = computed((): number => {
    let total = 0
    const socialMedia = calculateDesignSocialMedia()
    const video = calculateVideoRecurring()

    if (socialMedia.frequency === 'monthly') total += socialMedia.servicePrice
    total += video.videoMonthlyServicePrice

    return total
  })

  const weeklyTotal = computed((): number => {
    let total = 0
    const socialMedia = calculateDesignSocialMedia()
    const video = calculateVideoRecurring()

    if (socialMedia.frequency === 'weekly') total += socialMedia.servicePrice
    total += video.videoWeeklyServicePrice

    return total
  })

  const totalPrice = computed((): number => {
    return websitePrice.value + designPrice.value + videoPrice.value
  })

  return {
    calculateWebsitePrice: websitePrice,
    calculateDesignPrice: designPrice,
    calculateVideoPrice: videoPrice,
    websiteMaintenance,
    designSocialMediaService,
    videoServices,
    yearlyTotal,
    monthlyTotal,
    weeklyTotal,
    totalPrice,
  }
}
